import * as i18next from 'i18next'
import * as React from 'react'
import { Action, IOWidget, mk_widget } from 'widgets-for-react'
import { CMSState, Url } from '../../../../cms/blocks/types.gen'
import { PublicSiteState } from '../../../../public_site.main.mod'
import { ButtonData } from '../../../../wordpress/types_and_parser/extra_types.mod'
import { WordPress } from '../../../../wordpress/wordpress_api'
import { PublicSite_CustomPageData } from '../../../application.mod'
import { getImageAttribute } from '../../../utils/extra_images.mod'

export type CarouselBlockItem = {
  title: string
  intro: string
  image?: WordPress.MediaResponse
  link?: string
  label?: string
  is_video?: boolean
}

export type GridAttributes = {
  blockName: 'vidda/carousel'
  title: string
  intro: string
  primary_button?: ButtonData
  secondary_button?: ButtonData
  blocks: Array<CarouselBlockItem>
  show_random_items?: boolean
}

export type CarouselBlockProps<extra_images> = GridAttributes

export type LinkProps = { key?: string; title: string; url: Url; target?: '_blank' | '_self' }

export type CarouselBlockStyle = Partial<{
  block_classname: string
  inner_classname: string
  header_classname: string
  label_classname: string
  title_classname: string
  content_classname: string
  carousel_classname: string
  carousel_inner_classname: string
  carousel_list_classname: string
  carousel_item_classname: string
  carousel_figure_classname: string
  carousel_image_classname: string
  footer_classname: string
}>

function sample<T>(population:T[], k:number){
    /*
        Chooses k unique random elements from a population sequence or set.

        Returns a new list containing elements from the population while
        leaving the original population unchanged.  The resulting list is
        in selection order so that all sub-slices will also be valid random
        samples.  This allows raffle winners (the sample) to be partitioned
        into grand prize and second place winners (the subslices).

        Members of the population need not be hashable or unique.  If the
        population contains repeats, then each occurrence is a possible
        selection in the sample.

        To choose a sample in a range of integers, use range as an argument.
        This is especially fast and space efficient for sampling from a
        large population:   sample(range(10000000), 60)

        Sampling without replacement entails tracking either potential
        selections (the pool) in a list or previous selections in a set.

        When the number of selections is small compared to the
        population, then tracking selections is efficient, requiring
        only a small set and an occasional reselection.  For
        a larger number of selections, the pool tracking method is
        preferred since the list takes less space than the
        set and it doesn't suffer from frequent reselections.
    */

    if(!Array.isArray(population))
        throw new TypeError("Population must be an array.");
    var n = population.length;
    if(k < 0 || k > n)
        throw new RangeError("Sample larger than population or is negative");

    var result = new Array<T>(k);
    var setsize = 21;   // size of a small set minus size of an empty list

    if(k > 5)
        setsize += Math.pow(4, Math.ceil(Math.log(k * 3) / Math.log(4)))

    if(n <= setsize){
        // An n-length list is smaller than a k-length set
        var pool = population.slice();
        for(var i = 0; i < k; i++){          // invariant:  non-selected at [0,n-i)
            var j = Math.random() * (n - i) | 0;
            result[i] = pool[j];
            pool[j] = pool[n - i - 1];       // move non-selected item into vacancy
        }
    }else{
        var selected = new Set();
        for(var i = 0; i < k; i++){
            var j = Math.random() * n | 0;
            while(selected.has(j)){
                j = Math.random() * n | 0;
            }
            selected.add(j);
            result[i] = population[j];
        }
    }

    return result;
}

export function CarouselBlock<customBlockData, extra_images>(
  props: CarouselBlockProps<extra_images>,
  options?: { style: CarouselBlockStyle; key?: string }
): (
  s: CMSState<customBlockData, extra_images, PublicSite_CustomPageData>
) => IOWidget<PublicSiteState, Action<CMSState<customBlockData, extra_images, PublicSite_CustomPageData>>> {
  return (s: CMSState<customBlockData, extra_images, PublicSite_CustomPageData>) => (public_state: PublicSiteState) => {
    return mk_widget({
      run: (set_state) => {
        return (
          <section
            className="block block--carousel"
            key={options && options.key ? options.key : 'carousel_image-block'}
          >
            <div className="block__inner container--xl grid--block">
              <header className="block__header">
                {props.title && <h2 className="block__title typo--3xl">{props.title}</h2>}
                {props.intro && <p className="block__intro" dangerouslySetInnerHTML={{ __html: props.intro }}></p>}
              </header>

              {props.blocks && (
                <div className="block__content">
                  <div className="carousel">
                    <div className="carousel__inner">
                      <ul
                        id="js--imgcar"
                        className="carousel__list"
                        data-width={props.blocks.length * 320 + 120}
                        style={{ width: props.blocks.length * 320 + 120 + 'px' }}
                      >
                        {(props.show_random_items ? sample<CarouselBlockItem>(props.blocks, 12) : props.blocks).map(
                          (c) => {
                            return carouselRenderer<customBlockData, extra_images>(c, s, options)
                          }
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <footer className="block__footer">
              <button
                id="js--imgcar-prev"
                className={`button button--prev block__button ${
                  props.blocks.length * 320 + 120 <= window.innerWidth ? 'hide' : ''
                }`}
                aria-label={i18next.t('general:prev')}
                onClick={(e) => handleCarouselScroll('prev')}
              >
                &nbsp;
              </button>
              <button
                id="js--imgcar-next"
                className={`button button--next block__button ${
                  props.blocks.length * 320 + 120 <= window.innerWidth ? 'hide' : ''
                }`}
                aria-label={i18next.t('general:next')}
                onClick={(e) => handleCarouselScroll('next')}
              >
                &nbsp;
              </button>
            </footer>
          </section>
        )
      },
    })
  }
}

function carouselRenderer<customBlockData, extra_images>(
  props: CarouselBlockItem,
  cmsState: CMSState<customBlockData, extra_images, PublicSite_CustomPageData>,
  options?: {
    style: CarouselBlockStyle
    key?: string
  }
) {
  return props.image != null ? (
    <li className="carousel__item">
      <div className="carousel__wrap">
        <div className="carousel__figure fit-object">
          {props.image && (
            <img
              src={getImageAttribute(props.image, 'carousel', 'source_url')}
              width={110}
              height={102}
              alt={props.image.alt_text ? props.image.alt_text : 'image of a logo'}
            />
          )}
          </div>
      </div>
    </li>
  ) : null
}

let carouselPosition: number = 0

function handleCarouselScroll(direction: string) {
  const carousel: HTMLElement = document.querySelector('#js--imgcar')
  if (carousel == null) return
  const rightPosition: number = carousel.getBoundingClientRect().right - 15 // 15 = right margin of carousel list
  const leftPosition: number = carousel.getBoundingClientRect().left + 15 // 15 = left margin of carousel list

  if (direction == 'next') {
    if (rightPosition > window.innerWidth) {
      const remainingCarousel: number = rightPosition - window.innerWidth
      if (remainingCarousel >= 320 * 3) {
        carouselPosition = carouselPosition + 320 * 3
      } else {
        if (remainingCarousel < 320 * 3 && remainingCarousel > 0) {
          carouselPosition = carouselPosition + remainingCarousel
        } else if (remainingCarousel <= 0) {
          carouselPosition = carouselPosition
        }
      }
    }
  } else {
    if (leftPosition < 0) {
      const remainingCarousel: number = 0 - leftPosition
      if (remainingCarousel >= 320 * 3) {
        carouselPosition = carouselPosition - 320 * 3
      } else {
        if (remainingCarousel < 320 * 3 && remainingCarousel > 0) {
          carouselPosition = carouselPosition - remainingCarousel
        } else if (remainingCarousel <= 0) {
          carouselPosition = carouselPosition
        }
      }
    }
  }
  carousel.style.transform = 'translateX(-' + carouselPosition + 'px)'

  // HANDLE THE SHOWING/HIDING OF BUTTONS
  handleCarouselButtons(direction)
}

function handleCarouselButtons(direction: string) {
  const carousel: HTMLElement = document.querySelector('#js--imgcar')

  if (carousel == null) return
  if ((direction == 'scroll' || direction == 'load') && carousel.classList.contains('init')) return

  const carouselWidth: number = parseInt(carousel.getAttribute('data-width')) - 120
  const carouselPrev: HTMLElement = document.querySelector('#js--imgcar-prev')
  const carouselNext: HTMLElement = document.querySelector('#js--imgcar-next')

  if (carouselWidth <= window.innerWidth) {
    carouselPrev.classList.add('hide')
    carouselNext.classList.add('hide')
  } else {
    carouselPrev.classList.remove('hide')
    carouselNext.classList.remove('hide')
  }

  // SHOW/HIDE BUTTONS
  if (carouselPosition <= 0) {
    carouselPrev.setAttribute('disabled', 'disabled')
  } else {
    carouselPrev.removeAttribute('disabled')
  }

  const nextCarouselPosition: number =
    direction == 'next'
      ? carouselPosition + 320 * 3
      : direction == 'prev'
      ? carouselPosition - 320 * 3
      : carouselPosition
  const nextCarouselRightPosition: number =
    direction == 'next'
      ? carousel.getBoundingClientRect().right - 320 * 3
      : direction == 'prev'
      ? carousel.getBoundingClientRect().right + 320 * 3
      : carousel.getBoundingClientRect().right

  if (nextCarouselPosition >= carouselWidth || nextCarouselRightPosition <= window.innerWidth) {
    carouselNext.setAttribute('disabled', 'disabled')
  } else {
    carouselNext.removeAttribute('disabled')
  }

  carousel.classList.add('init')
}

document.addEventListener('scroll', () => handleCarouselButtons('scroll'))
window.addEventListener('resize', () => handleCarouselButtons(''))
window.addEventListener('load', () => handleCarouselButtons('load'))
